import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoader } from '../../../../context/LoaderContext';
import { OrdersService } from '../../../../services/OrdersService';
import { ToasterService } from '../../../../services/ToasterService';

export default function useOrderHistory() {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const customerId = useRef('');
  const [cachedOrders, setCachedOrders] = useState<any[]>([]);
  const [displayedOrders, setDisplayedOrders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const recordsPerPage = 10;
  const fetchedPagesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const user = localStorage.getItem('userLoginData');
    if (user) {
      const parsed = JSON.parse(user);
      if (parsed.customer_id) {
        customerId.current = parsed.customer_id;
      }
    }
  }, []);

  useEffect(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;

    if (fetchedPagesRef.current.has(currentPage)) {
      const dataSlice = cachedOrders.slice(startIndex, endIndex);
      setDisplayedOrders(dataSlice);
    } else {
      fetchOrders(currentPage);
    }
  }, [currentPage]);

  const fetchOrders = async (page: number) => {
    const offset = (page - 1) * recordsPerPage;
    try {
      setLoading(true);
      const resp = await OrdersService.History(customerId.current, offset, recordsPerPage);
      setLoading(false);

      if (resp?.message?.orders?.length > 0) {
        const newOrders = resp.message.orders;
        fetchedPagesRef.current.add(page);

        /** Update cache */
        setCachedOrders(prev => {
          const merged = [...prev];
          for (let i = 0; i < newOrders.length; i++) {
            merged[offset + i] = newOrders[i];
          }
          return merged;
        });
        setDisplayedOrders(newOrders);
        if (resp.message.order_count) {
          setTotalRecords(resp.message.order_count);
        }
      }
    } catch {
      setLoading(false);
      ToasterService.Error('Failed to fetch orders.');
      // console.error(err);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return {
    displayedOrders,
    currentPage,
    setCurrentPage,
    totalPages,
    recordsPerPage,
    navigate,
  };
}
